export const riboProject = {
  id: 1, 
  slug: 'ribo', 
  title: 'RIBO', 
  description: 'Licenses and regulates insurance brokers in Ontario', 
  tags: ['website redesign', 'IA', 'accessibility'], 
  image: '/ribo/RIBO - Cover.png', 
  logo: '/ribo/RIBO - Logo.png',
  heroLogo: '/ribo/RIBO - Logo No BG.svg',
  heroImage: '/ribo/RIBO - Header.png',
  heroDescription: 'A modern platform that connects users with innovative solutions.',
  headerBackground: '#213553',
  meta: {
    role: 'UI/UX Designer',
    timeline: '8 months',
    tools: 'Figma, Octopus, Zeplin',
  },
  liveUrl: 'https://ribo.com',
  overview: {
    title: 'Overview',
    paragraphs: [
      'RIBO is the regulatory body for insurance brokers in Ontario, responsible for licensing brokers, providing regulatory resources, and supporting consumers through education and complaints processes.',
    ],
  },
  goal: {
    title: 'Goal',
    description: 'Redesign the RIBO website to reduce cognitive load, clarify user pathways, and establish consistent interaction patterns - enabling users to quickly understand the site structure and complete high-intent tasks with confidence.',
  },
  challenge: {
    title: 'Challenges',
    description: 'Despite having comprehensive content, the RIBO website made it difficult for users to confidently navigate, understand priorities, and predict system behavior. A UX audit revealed that many issues stemmed from structural and interaction‑level problems rather than purely visual ones.',
    problemAreas: [
      'Navigation & IA',
      'Visual hierarchy & content prioritization',
      'Content density & readability issues',
    ],
  },
  problems: [
    {
      id: 'problem-1',
      title: 'Problem 1: Navigation & IA Breakdown',
      whatWasHappening: 'Users struggled to understand where to go and how content was organized. The primary navigation exposed too many options at once, mixed user groups, and failed to prioritize high‑intent tasks.',
      keyFindings: [
        'Consumer‑facing content was mixed with RIBO‑internal information such as About, Staff, and Council pages, creating confusion',
        'High‑priority actions like filing a complaint were positioned last in the menu, reducing discoverability',
        'Excessive menu depth and density increased cognitive load',
        'Active states and orientation cues were missing, making it difficult for users to know where they were',
        'Navigation complexity increased further on mobile due to layered menus and inconsistent behavior',
      ],
      whyItMattered: 'Without a clear mental model of the site structure, users were forced into trial‑and‑error navigation—especially problematic for a regulatory website where users expect clarity and authority.',
      designFocus: [
        'Clearly separate Consumers and Brokers at the top level',
        'Reduce menu depth and surface key tasks earlier',
        'Improve orientation with clearer hierarchy and active states',
        'Ensure navigation patterns scale cleanly from desktop to mobile',
      ],
      oldStructure: '/ribo/RIBO - Old Structure.png',
      newStructure: '/ribo/RIBO - New Structure.png',
    },
    {
      id: 'problem-2',
      title: 'Problem 2: Weak Visual Hierarchy & Content Prioritization',
      whatWasHappening: 'Important information competed with secondary content, making it difficult for users to quickly understand the site\'s purpose or identify next steps.',
      keyFindings: [
        'The homepage value proposition was visually fragmented and hard to scan',
        'The primary CTA ("About RIBO") did not align with users\' main tasks',
        'Overuse of bold text reduced readability and hierarchy',
        'Dense layouts and small text sizes impacted legibility, especially in forms',
        'Decorative images pushed meaningful content below the fold without adding value',
      ],
      designFocus: [
        'Establish a clear typographic scale and layout rhythm',
        'Prioritize primary actions visually',
        'Reduce noise by deprioritizing non‑essential content',
        'Improve spacing and alignment for readability',
      ],
      oldStructure: '/ribo/RIBO - Old Page.png',
      newStructure: '/ribo/RIBO - New Page.png',
    },
    {
      id: 'problem-3',
      title: 'Problem 3: Content Density & Readability Issues',
      whatWasHappening: 'Many pages on the RIBO website presented users with large volumes of dense text and limited visual structure. Critical information, requirements, and next steps were often embedded within long paragraphs, making it difficult for users to quickly scan, understand, and act - particularly during high-stakes tasks such as licensing and complaints.',
      keyFindings: [
        'Pages rely heavily on long, uninterrupted text blocks, with limited use of headings, lists, or visual grouping, reducing scan efficiency',
        'Small font sizes and tight line spacing negatively impact readability for instructional and form-related content',
        'Important guidance and requirements are frequently buried mid-paragraph, rather than surfaced through hierarchy or emphasis',
        'Content density becomes more problematic on mobile, where reduced screen width further increases cognitive load',
      ],
      whyItMattered: 'Dense, unstructured content creates barriers to comprehension and action—especially problematic for regulatory information where clarity and accuracy are critical. Users need to quickly find and understand requirements without wading through walls of text.',
      designFocus: [
        'Break large content blocks into scannable sections',
        'Introduce a clearer typographic hierarchy to surface key information',
        'Use spacing, headings, and lists to improve readability',
        'Apply progressive disclosure so users see only what they need at each step',
        'Optimize layouts for mobile readability without sacrificing clarity',
      ],
      oldStructure: '/ribo/RIBO - Old Accordion.png',
      newStructure: '/ribo/RIBO - New Accordion.png',
    },
  ],
  results: {
    title: 'Results',
    description: 'The redesign transformed the RIBO website into a clearer, more task-focused experience by addressing foundational usability issues rather than surface-level visuals. Simplified navigation and clearer audience pathways helped users quickly understand where they belong, while improved visual hierarchy and reduced content density made information easier to scan and act on. Consistent interaction patterns and system feedback increased predictability and user confidence across high-stakes workflows. Together, these changes support faster task completion, reduced confusion, and a more trustworthy experience that better reflects RIBO\'s role as a regulatory authority.',
    hpImage: '/ribo/RIBO - HP.png',
  },
};
